/**
 * Lumi Holocard voice moments.
 *
 * These are OPTIONAL, learner-CLICKED support moments (never auto-play).
 * They live in the lesson experience as a small "Ask Lumi" holocard and
 * are voiced by Lumi Vanta (ElevenLabs voice `KR1TkIhkSykEjI4B0DtH`).
 *
 * Kept separate from `lessonVoiceMap` (auto-play lesson voice moments)
 * and `instructorVoiceMap` (module voice moments) so the three systems
 * cannot collide. Server-side, the `instructor-voice` edge function
 * mirrors these transcripts as part of its trusted allowlist so the
 * client only ever sends `{ momentId }` — never free text.
 */

export interface LumiHolocardVoiceMoment {
  momentId: string;
  lessonId: string;
  lessonSlug: string;
  /** Where inside the lesson the holocard is shown. */
  stage: "infographic" | "pre_decision" | "post_decision" | "completion";
  /** Short pitch shown on the card face, before the learner taps it. */
  prompt: string;
  /** Full spoken/captioned transcript revealed after the learner taps. */
  transcript: string;
  speaker: "lumi";
  kind: "lumi_holocard";
  autoPlay: false;
}

function moment(
  lessonId: string,
  lessonSlug: string,
  stage: LumiHolocardVoiceMoment["stage"],
  prompt: string,
  transcript: string,
): LumiHolocardVoiceMoment {
  return {
    momentId: `lumi.${lessonSlug}.${stage === "pre_decision" ? "decisionHint" : stage === "post_decision" ? "postDecisionReflect" : stage === "completion" ? "completionNote" : "infographicHelp"}`,
    lessonId,
    lessonSlug,
    stage,
    prompt,
    transcript,
    speaker: "lumi",
    kind: "lumi_holocard",
    autoPlay: false,
  };
}

export const LUMI_HOLOCARD_MOMENTS: LumiHolocardVoiceMoment[] = [
  moment(
    "709b0a91-2103-4ddf-9a70-e4ee5dae0b41",
    "needsWants",
    "infographic",
    "Need help telling needs from wants?",
    "A need protects your stability. A want adds comfort. The trick is noticing when comfort starts pretending to be stability.",
  ),
  moment(
    "f1ec70b9-2619-41b4-b2e8-f89425bf4e1b",
    "opportunityCost",
    "pre_decision",
    "Not sure how to weigh it?",
    "Every choice has a shadow — the thing you didn't pick. Ask what the shadow costs you a year from now, not just tonight.",
  ),
  moment(
    "9979b6d9-b308-49f4-b9a5-29e5a6a7ff3f",
    "financialTradeoffs",
    "pre_decision",
    "Stuck between two solid options?",
    "A tradeoff isn't a loss — it's a trade. Name what you're gaining and what you're giving up out loud, and the right move usually shows itself.",
  ),
  moment(
    "e29e634d-e035-42fb-8568-a5241a02d595",
    "delayedGratification",
    "pre_decision",
    "Tempted to grab it now?",
    "Waiting is a skill, not a punishment. The version of you a month from now is the one who has to live with tonight's choice.",
  ),
  moment(
    "d2b87cb1-7774-4029-87e0-be51ab14840e",
    "cashFlow",
    "post_decision",
    "Want a quick reflection?",
    "Cash flow rewards the boring habit: money in, money out, on a rhythm you can name. If you can name it, you can defend it.",
  ),
  moment(
    "c6d62ce8-0b9e-40ff-b09d-64bcda1f6210",
    "creditScoreBasics",
    "infographic",
    "Need a plain-language recap?",
    "Your score is a scoreboard for one thing: do you keep small promises to lenders over and over? That's it. That's the whole game.",
  ),
  moment(
    "db6fdc3b-f16d-4fe5-a750-c65c2c8dc5a1",
    "borrowingMoney",
    "pre_decision",
    "Thinking about borrowing?",
    "Borrowed money always comes with a second price tag: time. Ask what you're really paying, and how long future-you has to keep paying it.",
  ),
  moment(
    "fcbdef95-7194-4921-8cb7-dfdfebda0ee0",
    "minimumPaymentsTrap",
    "pre_decision",
    "Minimum payment looks easy?",
    "The minimum is designed to keep you paying, not to get you free. Every dollar above it is a dollar buying back your future.",
  ),
  moment(
    "e1e29e49-0074-48f5-ba15-70374284867e",
    "urgencyAsRedFlag",
    "pre_decision",
    "Feel the pressure to act now?",
    "Urgency is the oldest trick in the book. Real opportunities can wait five minutes. Scams almost never can.",
  ),
  moment(
    "846aa833-7dd2-4059-892a-6fbebf6a2c2e",
    "tooGoodToBeTrue",
    "pre_decision",
    "Sounds too good to pass up?",
    "If the reward looks huge and the risk looks invisible, the risk isn't gone — it's just hidden. Trust the math, not the pitch.",
  ),
];

const HOLOCARD_BY_ID: Record<string, LumiHolocardVoiceMoment> = Object.fromEntries(
  LUMI_HOLOCARD_MOMENTS.map((m) => [m.momentId, m]),
);

const HOLOCARD_BY_LESSON: Record<string, LumiHolocardVoiceMoment[]> = (() => {
  const acc: Record<string, LumiHolocardVoiceMoment[]> = {};
  for (const m of LUMI_HOLOCARD_MOMENTS) (acc[m.lessonId] ||= []).push(m);
  return acc;
})();

export function getLumiHolocardMoment(momentId: string): LumiHolocardVoiceMoment | undefined {
  return HOLOCARD_BY_ID[momentId];
}

/** Holocards for a lesson, optionally filtered to a single stage. */
export function getLumiHolocardMomentsFor(
  lessonId: string | null | undefined,
  stage?: LumiHolocardVoiceMoment["stage"],
): LumiHolocardVoiceMoment[] {
  if (!lessonId) return [];
  const bucket = HOLOCARD_BY_LESSON[lessonId] ?? [];
  return stage ? bucket.filter((m) => m.stage === stage) : bucket;
}

export function getLumiHolocardMomentIds(lessonId: string | null | undefined): string[] {
  return getLumiHolocardMomentsFor(lessonId).map((m) => m.momentId);
}